/**
 * Integrations API Service
 *
 * This service provides mock data for available integrations.
 * When ready to connect to a real backend, replace the mock implementation
 * with actual fetch calls to your API endpoints while preserving the return types.
 */

import {
  BarChart3,
  Search,
  PieChart,
  Slack,
  Mail,
  LineChart,
  Package,
  Database,
  Globe,
  ShoppingCart,
  FileText,
  Users,
  Target,
  TrendingUp,
  FileCode2,
  Linkedin,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type IntegrationStatus = 'live' | 'in-progress' | 'planned';
export type IntegrationCategory =
  | 'analytics'
  | 'collaboration'
  | 'bi'
  | 'web-commerce'
  | 'marketing-crm'
  | 'publishing';

export interface Integration {
  id: string;
  name: string;
  category: IntegrationCategory;
  status: IntegrationStatus;
  description: string; // One-line outcome-oriented
  icon: LucideIcon;
  detailDescription: string; // 2-3 sentences for detail view
  dataFlow: {
    from: string[];
    to: string[];
  };
  benefits: string[];
  statusNote: string; // Status-specific messaging
  setupLocation: string;
  documentationUrl?: string;
  /**
   * If true, the detail panel will render the Brand Publisher connect flow
   * (right-slide credentials form) instead of a generic status blurb. The
   * `publishingDestination` field tells the panel which MCP destination this
   * card maps to.
   */
  isPublishingDestination?: boolean;
  publishingDestination?: 'hashnode' | 'wordpress' | 'linkedin';
}

/**
 * Fetch all available integrations
 *
 * @returns Promise with array of Integration objects
 *
 * TODO: Replace mock implementation with:
 * return fetch('/api/integrations')
 *   .then(res => res.json());
 */
export async function getIntegrations(): Promise<Integration[]> {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 400));

  return [
    // ═══════════════════════════════════════════════════════════════════════
    //  PUBLISHING (Brand Publisher destinations)
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: 'hashnode',
      name: 'Hashnode',
      category: 'publishing',
      status: 'live',
      description: 'Publish AI-drafted articles directly to your Hashnode blog',
      icon: FileCode2,
      detailDescription: 'Connect your Hashnode developer blog so Upmode can publish long-form articles straight to your publication. Perfect for engineering-led content, product deep-dives, and thought leadership aimed at a technical audience.',
      dataFlow: {
        from: ['Approved content drafts', 'Article titles and markdown', 'Tags and slugs', 'Canonical URLs'],
        to: ['Hashnode publishPost mutation', 'Your publication feed', 'Post URL and ID'],
      },
      benefits: [
        'Zero-latency publishing — goes live the moment you approve',
        'Self-serve credentials — just paste a Personal Access Token, no app review',
        'Your content, your account — fully transparent, brand-owned publishing',
      ],
      statusNote: 'This destination is live and self-serve. Paste your Hashnode Personal Access Token and blog URL to connect.',
      setupLocation: 'Integrations > Publishing > Hashnode',
      isPublishingDestination: true,
      publishingDestination: 'hashnode',
    },
    {
      id: 'publishing-wordpress',
      name: 'WordPress',
      category: 'publishing',
      status: 'live',
      description: 'Publish AI-drafted posts to your WordPress site',
      icon: Globe,
      detailDescription: 'Connect your self-hosted or managed WordPress site using an application password. Upmode publishes polished HTML posts with titles, excerpts, and custom statuses — draft, published, or scheduled.',
      dataFlow: {
        from: ['Approved content drafts', 'HTML post bodies', 'Titles and excerpts', 'Post status settings'],
        to: ['WordPress REST API /wp/v2/posts', 'Post metadata', 'Published/draft states'],
      },
      benefits: [
        'Works with any WordPress 5.6+ site — self-hosted or managed',
        'Credentials stay on your side via application passwords',
        'Full control over whether posts go live immediately or stay as drafts',
      ],
      statusNote: 'This destination is live and self-serve. Create an application password in your WordPress profile to connect.',
      setupLocation: 'Integrations > Publishing > WordPress',
      isPublishingDestination: true,
      publishingDestination: 'wordpress',
    },
    {
      id: 'publishing-linkedin',
      name: 'LinkedIn',
      category: 'publishing',
      status: 'live',
      description: 'Post AI-drafted updates to LinkedIn profile or Company Page',
      icon: Linkedin,
      detailDescription: 'Publish short-form thought leadership posts to your personal LinkedIn profile or your brand\'s Company Page. Bring your own LinkedIn developer app and access token — Upmode handles the rest.',
      dataFlow: {
        from: ['Approved post text', 'Visibility settings', 'Author URN (person or organization)'],
        to: ['LinkedIn /v2/ugcPosts endpoint', 'Your profile or Company Page feed', 'Share URN'],
      },
      benefits: [
        'Supports both personal posts and Company Page publishing',
        'Character-count aware — respects the 1,300-char "see more" fold',
        'Token expiry tracking with proactive reconnect nudges',
      ],
      statusNote: 'This destination is live and self-serve. Requires a LinkedIn developer app with the Share on LinkedIn product.',
      setupLocation: 'Integrations > Publishing > LinkedIn',
      isPublishingDestination: true,
      publishingDestination: 'linkedin',
    },

    // ═══════════════════════════════════════════════════════════════════════
    //  ANALYTICS & MEASUREMENT
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: 'google-analytics',
      name: 'Google Analytics (GA4)',
      category: 'analytics',
      status: 'planned',
      description: 'Connect AI-driven demand signals to Google Analytics',
      icon: BarChart3,
      detailDescription: 'Send AI recommendation events, brand mention signals, and referral conversation metrics directly to Google Analytics 4. Track how AI-driven insights correlate with your existing web analytics and user behavior data.',
      dataFlow: {
        from: ['AI recommendation events', 'Brand mention signals', 'Share of voice metrics', 'Referral conversation data'],
        to: ['GA4 custom events', 'Custom dimensions', 'User properties', 'Enhanced measurement']
      },
      benefits: [
        'Correlate AI insights with web traffic and conversion data',
        'Build custom reports combining AI signals and GA4 metrics',
        'Track the business impact of AI recommendation changes over time'
      ],
      statusNote: 'This integration is planned and not yet available. Contact your account manager to express interest.',
      setupLocation: 'Not yet available',
    },
    {
      id: 'google-search-console',
      name: 'Google Search Console',
      category: 'analytics',
      status: 'planned',
      description: 'Cross-reference AI recommendations with organic search performance',
      icon: Search,
      detailDescription: 'Compare how AI models recommend your brand against your organic search visibility. Identify opportunities where strong AI sentiment doesn\'t match search presence, and vice versa.',
      dataFlow: {
        from: ['Brand perception scores', 'Recommendation frequency', 'Competitor comparison data'],
        to: ['Search Console API queries', 'Position tracking', 'Click-through analysis']
      },
      benefits: [
        'Identify gaps between AI recommendation strength and SEO performance',
        'Discover high-value keywords where AI already recommends your brand',
        'Align content strategy across AI and search channels'
      ],
      statusNote: 'This integration is planned and not yet available. Contact your account manager to express interest.',
      setupLocation: 'Not yet available',
    },
    {
      id: 'adobe-analytics',
      name: 'Adobe Analytics',
      category: 'analytics',
      status: 'planned',
      description: 'Send AI performance and recommendation data to Adobe Analytics',
      icon: PieChart,
      detailDescription: 'Export AI-driven brand signals, recommendation events, and perception metrics to Adobe Analytics. Build unified dashboards that combine AI intelligence with your existing digital analytics.',
      dataFlow: {
        from: ['AI perception scores', 'Recommendation events', 'Model-specific metrics', 'Competitive position data'],
        to: ['Adobe Analytics custom events', 'eVars and props', 'Processing rules', 'Calculated metrics']
      },
      benefits: [
        'Unified view of AI and digital analytics in one platform',
        'Segment audiences by AI recommendation exposure',
        'Build attribution models that include AI referral signals'
      ],
      statusNote: 'This integration is on our roadmap. Expected availability Q2 2026. Contact your account manager to express interest.',
      setupLocation: 'Settings > Integrations > Analytics (when available)',
    },

    // ═══════════════════════════════════════════════════════════════════════
    //  COLLABORATION & ALERTS
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: 'slack',
      name: 'Slack',
      category: 'collaboration',
      status: 'planned',
      description: 'Send AI referral and recommendation signals to Slack',
      icon: Slack,
      detailDescription: 'Receive real-time alerts when AI models change how they recommend your brand. Get notified about perception score changes, new competitor mentions, or critical accuracy risks—delivered where your team already works.',
      dataFlow: {
        from: ['Perception score changes', 'New competitor mentions', 'Critical action items', 'Daily briefing summaries'],
        to: ['Slack channels', 'Direct messages', 'Threaded notifications', 'Rich message formatting']
      },
      benefits: [
        'React immediately to AI recommendation changes',
        'Keep marketing and product teams aligned on AI performance',
        'Route critical alerts to the right channels automatically'
      ],
      statusNote: 'This integration is planned and not yet available. Contact your account manager to express interest.',
      setupLocation: 'Not yet available',
    },
    {
      id: 'microsoft-teams',
      name: 'Microsoft Teams',
      category: 'collaboration',
      status: 'in-progress',
      description: 'Deliver AI performance alerts and insights to Microsoft Teams',
      icon: Package,
      detailDescription: 'Bring AI brand monitoring into Microsoft Teams. Receive alerts about perception changes, share weekly reports with stakeholders, and keep your organization informed about how AI models represent your brand.',
      dataFlow: {
        from: ['Weekly performance reports', 'Alert notifications', 'Action item summaries', 'Competitive intelligence'],
        to: ['Teams channels', 'Adaptive cards', 'Bot notifications', 'Connector webhooks']
      },
      benefits: [
        'Enterprise-grade notifications for Microsoft-first organizations',
        'Share AI insights with stakeholders in existing Teams workflows',
        'Centralize alerts across multiple monitoring systems'
      ],
      statusNote: 'This integration is currently in development. Beta access available Q1 2026. Contact support to join the beta program.',
      setupLocation: 'Settings > Integrations > Collaboration (beta)',
    },
    {
      id: 'email-notifications',
      name: 'Email Notifications',
      category: 'collaboration',
      status: 'planned',
      description: 'Receive AI performance reports and alerts via email',
      icon: Mail,
      detailDescription: 'Configure custom email alerts for perception score changes, new action items, or daily performance summaries. Route different alert types to different team members based on role and responsibility.',
      dataFlow: {
        from: ['Daily/weekly summaries', 'Threshold-based alerts', 'Action item notifications', 'Report exports'],
        to: ['Individual email addresses', 'Distribution lists', 'HTML-formatted reports', 'CSV attachments']
      },
      benefits: [
        'Keep stakeholders informed without requiring platform access',
        'Schedule automated reports for executive review',
        'Customize alert routing by team, role, and priority level'
      ],
      statusNote: 'This integration is planned and not yet available. Contact your account manager to express interest.',
      setupLocation: 'Not yet available',
    },

    // ═══════════════════════════════════════════════════════════════════════
    //  BI & DATA WAREHOUSES
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: 'looker',
      name: 'Looker',
      category: 'bi',
      status: 'planned',
      description: 'Export AI performance and recommendation data to Looker',
      icon: LineChart,
      detailDescription: 'Connect your AI performance data to Looker for custom dashboards and advanced analysis. Build unified views that combine AI perception metrics with revenue, product usage, and customer data.',
      dataFlow: {
        from: ['Time-series perception data', 'Model-specific metrics', 'Competitor benchmarks', 'Action item history'],
        to: ['Looker data models', 'Custom explores', 'Dashboards', 'Scheduled reports']
      },
      benefits: [
        'Build custom dashboards combining AI and business metrics',
        'Perform advanced correlation and regression analysis',
        'Share AI insights across your organization with familiar BI tools'
      ],
      statusNote: 'This integration is planned and not yet available. Contact your account manager to express interest.',
      setupLocation: 'Not yet available',
    },
    {
      id: 'tableau',
      name: 'Tableau',
      category: 'bi',
      status: 'planned',
      description: 'Visualize AI performance data in Tableau dashboards',
      icon: BarChart3,
      detailDescription: 'Export AI perception scores, recommendation trends, and competitive intelligence to Tableau. Create interactive visualizations that help stakeholders understand AI performance at a glance.',
      dataFlow: {
        from: ['Perception score time series', 'Share of voice data', 'Model comparison metrics', 'Sentiment analysis'],
        to: ['Tableau data sources', 'Live connections', 'Extracts', 'Published workbooks']
      },
      benefits: [
        'Create executive-ready visualizations of AI performance',
        'Combine AI metrics with sales, marketing, and product data',
        'Enable self-service exploration of AI trends'
      ],
      statusNote: 'This integration is planned and not yet available. Contact your account manager to express interest.',
      setupLocation: 'Not yet available',
    },
    {
      id: 'power-bi',
      name: 'Power BI',
      category: 'bi',
      status: 'in-progress',
      description: 'Connect AI insights to Microsoft Power BI',
      icon: PieChart,
      detailDescription: 'Bring AI performance data into Power BI for enterprise reporting and analysis. Build dashboards that integrate AI perception metrics with your existing business intelligence infrastructure.',
      dataFlow: {
        from: ['Brand perception metrics', 'Recommendation analytics', 'Competitor tracking', 'Model performance data'],
        to: ['Power BI datasets', 'DirectQuery connections', 'Published reports', 'Embedded dashboards']
      },
      benefits: [
        'Enterprise BI integration for Microsoft-centric organizations',
        'Combine AI insights with CRM, ERP, and financial data',
        'Publish AI performance reports to organizational portals'
      ],
      statusNote: 'This integration is currently in development. Beta available Q1 2026. Contact your account manager for early access.',
      setupLocation: 'Settings > Integrations > BI & Data (beta)',
    },
    {
      id: 'snowflake',
      name: 'Snowflake',
      category: 'bi',
      status: 'planned',
      description: 'Stream AI performance data to Snowflake data warehouse',
      icon: Database,
      detailDescription: 'Export raw AI performance data directly to your Snowflake data warehouse. Enable advanced analysis, machine learning, and data science workflows on top of your AI perception metrics.',
      dataFlow: {
        from: ['Raw AI response data', 'Parsed metrics', 'Time-series events', 'Model metadata'],
        to: ['Snowflake tables', 'Streaming ingestion', 'Data shares', 'External tables']
      },
      benefits: [
        'Full control over data retention and analysis',
        'Enable data science and ML workflows on AI perception data',
        'Integrate with existing data pipelines and transformations'
      ],
      statusNote: 'This integration is on our roadmap. Expected Q3 2026. We are evaluating customer demand—contact us to express interest.',
      setupLocation: 'Settings > Integrations > BI & Data (when available)',
    },
    {
      id: 'bigquery',
      name: 'Google BigQuery',
      category: 'bi',
      status: 'planned',
      description: 'Export AI data to BigQuery for large-scale analysis',
      icon: Database,
      detailDescription: 'Stream AI performance metrics directly to Google BigQuery. Run SQL queries, build ML models, and perform large-scale analysis on how AI models perceive and recommend your brand over time.',
      dataFlow: {
        from: ['Event-level AI data', 'Perception metrics', 'Model responses', 'Aggregated analytics'],
        to: ['BigQuery datasets', 'Streaming inserts', 'Scheduled transfers', 'Partitioned tables']
      },
      benefits: [
        'SQL-based analysis of AI perception trends',
        'Combine with Google Marketing Platform and GA4 data',
        'Build predictive models using BigQuery ML'
      ],
      statusNote: 'This integration is planned for late 2026. Register interest through your account team to influence prioritization.',
      setupLocation: 'Settings > Integrations > BI & Data (when available)',
    },

    // ═══════════════════════════════════════════════════════════════════════
    //  WEB, COMMERCE & CMS
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: 'wordpress',
      name: 'WordPress',
      category: 'web-commerce',
      status: 'planned',
      description: 'Optimize WordPress content based on AI recommendation insights',
      icon: Globe,
      detailDescription: 'Surface AI-driven content recommendations directly in WordPress. Identify which topics and keywords AI models associate with your brand, and optimize your content strategy accordingly.',
      dataFlow: {
        from: ['AI topic associations', 'Keyword recommendations', 'Content gap analysis', 'Competitive insights'],
        to: ['WordPress admin dashboard', 'Post metadata', 'SEO plugin integration', 'Content suggestions']
      },
      benefits: [
        'Optimize content for AI discoverability and recommendation',
        'Identify high-value topics where AI already recommends your brand',
        'Align WordPress content strategy with AI perception goals'
      ],
      statusNote: 'This integration is in planning stages. Expected Q3 2026. We\'re evaluating plugin architecture and WordPress compatibility.',
      setupLocation: 'WordPress Plugin Directory (when available)',
    },
    {
      id: 'webflow',
      name: 'Webflow',
      category: 'web-commerce',
      status: 'planned',
      description: 'Inform Webflow site optimization with AI insights',
      icon: Globe,
      detailDescription: 'Bring AI perception data into Webflow CMS. Understand which pages and content types AI models reference when recommending your brand, and optimize your site structure for AI discoverability.',
      dataFlow: {
        from: ['Page performance metrics', 'Content recommendations', 'Brand messaging insights', 'Topic associations'],
        to: ['Webflow CMS collections', 'Custom fields', 'Designer API', 'Analytics dashboard']
      },
      benefits: [
        'Design and structure sites for AI recommendation optimization',
        'Track which Webflow content influences AI model outputs',
        'A/B test messaging based on AI perception data'
      ],
      statusNote: 'This integration is planned for Q4 2026. We are working with Webflow\'s API team to design the optimal integration pattern.',
      setupLocation: 'Webflow App Marketplace (when available)',
    },
    {
      id: 'shopify',
      name: 'Shopify',
      category: 'web-commerce',
      status: 'in-progress',
      description: 'Connect AI recommendation signals to Shopify commerce data',
      icon: ShoppingCart,
      detailDescription: 'Understand how AI recommendations correlate with Shopify sales and customer behavior. Track the revenue impact of improved AI perception and optimize product content for AI-driven discovery.',
      dataFlow: {
        from: ['AI recommendation events', 'Brand perception scores', 'Product mention tracking'],
        to: ['Shopify customer events', 'Product metadata', 'Analytics dashboard', 'Marketing automation']
      },
      benefits: [
        'Measure revenue impact of AI recommendation improvements',
        'Optimize product descriptions for AI discoverability',
        'Identify products that AI models recommend most frequently'
      ],
      statusNote: 'This integration is in active development. Private beta Q2 2026. Contact sales for early access consideration.',
      setupLocation: 'Shopify App Store (beta)',
    },
    {
      id: 'contentful',
      name: 'Contentful',
      category: 'web-commerce',
      status: 'planned',
      description: 'Enrich Contentful content strategy with AI perception data',
      icon: FileText,
      detailDescription: 'Integrate AI topic recommendations and perception insights directly into Contentful. Guide content creation and optimization based on how AI models understand and recommend your brand.',
      dataFlow: {
        from: ['Topic recommendations', 'Content gap analysis', 'Messaging effectiveness', 'Brand positioning insights'],
        to: ['Contentful entries', 'Custom fields', 'Content model metadata', 'Workflow automation']
      },
      benefits: [
        'Create content optimized for AI model understanding',
        'Identify content gaps where AI lacks information about your brand',
        'Track content effectiveness in AI recommendation contexts'
      ],
      statusNote: 'This integration is planned for late 2026. We are evaluating headless CMS integration patterns across multiple platforms.',
      setupLocation: 'Contentful App Framework (when available)',
    },
    {
      id: 'sanity',
      name: 'Sanity',
      category: 'web-commerce',
      status: 'planned',
      description: 'Connect AI insights to Sanity content operations',
      icon: FileText,
      detailDescription: 'Bring AI-driven content recommendations into Sanity Studio. Understand which content types and topics improve AI model perception, and guide editorial strategy with AI intelligence.',
      dataFlow: {
        from: ['Content performance insights', 'Topic recommendations', 'Brand messaging analysis', 'Competitive content gaps'],
        to: ['Sanity documents', 'Custom schemas', 'Studio dashboard', 'GROQ queries']
      },
      benefits: [
        'Editorial workflow informed by AI perception data',
        'Structured content optimized for AI comprehension',
        'Real-time feedback on content effectiveness in AI contexts'
      ],
      statusNote: 'This integration is on our roadmap for Q4 2026. We are assessing customer demand and technical feasibility.',
      setupLocation: 'Sanity Plugin Directory (when available)',
    },

    // ═══════════════════════════════════════════════════════════════════════
    //  MARKETING & CRM
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: 'hubspot',
      name: 'HubSpot',
      category: 'marketing-crm',
      status: 'planned',
      description: 'Sync AI performance signals with HubSpot CRM and marketing',
      icon: Users,
      detailDescription: 'Connect AI brand perception data to HubSpot contacts, companies, and deals. Enrich your CRM with AI recommendation scores, track which customers were influenced by AI referrals, and measure marketing impact on AI perception.',
      dataFlow: {
        from: ['Brand perception scores', 'AI referral events', 'Competitive position data', 'Action item completion'],
        to: ['HubSpot contact properties', 'Company records', 'Deal stages', 'Custom events', 'Workflows']
      },
      benefits: [
        'Enrich CRM data with AI perception metrics',
        'Track which deals originated from AI recommendations',
        'Trigger marketing workflows based on AI perception changes'
      ],
      statusNote: 'This integration is planned and not yet available. Contact your account manager to express interest.',
      setupLocation: 'Not yet available',
    },
    {
      id: 'salesforce',
      name: 'Salesforce',
      category: 'marketing-crm',
      status: 'in-progress',
      description: 'Integrate AI insights with Salesforce CRM data',
      icon: Target,
      detailDescription: 'Bring AI brand intelligence into Salesforce. Create custom objects for AI perception data, track how AI recommendations influence pipeline, and build reports that connect sales outcomes to AI performance.',
      dataFlow: {
        from: ['Perception metrics', 'Recommendation frequency', 'Competitive intelligence', 'Market trends'],
        to: ['Salesforce custom objects', 'Account fields', 'Opportunity data', 'Einstein Analytics']
      },
      benefits: [
        'Sales teams see AI perception alongside traditional CRM data',
        'Attribute pipeline and revenue to AI recommendation improvements',
        'Build unified reports across sales and AI performance'
      ],
      statusNote: 'This integration is in active development. Private beta launching Q2 2026. Contact your account manager for beta access.',
      setupLocation: 'Salesforce AppExchange (beta)',
    },
    {
      id: 'marketo',
      name: 'Marketo',
      category: 'marketing-crm',
      status: 'planned',
      description: 'Connect AI brand signals to Marketo marketing automation',
      icon: TrendingUp,
      detailDescription: 'Trigger Marketo campaigns and workflows based on AI perception changes. Segment audiences by AI recommendation exposure, and measure how marketing activities influence AI model perception.',
      dataFlow: {
        from: ['Perception score changes', 'AI referral signals', 'Competitive position shifts', 'Brand sentiment data'],
        to: ['Marketo lead records', 'Custom fields', 'Smart campaigns', 'Program tokens', 'Analytics']
      },
      benefits: [
        'Automate marketing responses to AI perception changes',
        'Segment leads by AI recommendation awareness',
        'Measure marketing ROI impact on AI brand perception'
      ],
      statusNote: 'This integration is planned for Q3 2026 pending customer demand validation. Contact us to express interest.',
      setupLocation: 'Settings > Integrations > Marketing & CRM (when available)',
    },
  ];
}